
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { Logo } from "@/components/common/Logo";
import { useAuth } from "@/hooks/auth/useAuth";
import { useToast } from "@/hooks/use-toast";
import { useState, useEffect, useCallback, useMemo, memo } from "react";
import { useLocation } from "react-router-dom";
import { DesktopNavMenu } from "./navbar/DesktopNavMenu";
import { MobileNavbar } from "./navbar/MobileNavbar";
import { AuthButtons } from "./navbar/AuthButtons";
import { LoadingNavbar } from "./navbar/LoadingNavbar";

const NavbarComponent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, signOut, isLoading, isAuthenticated, isInitialized, isAdmin, viewAsUser, toggleViewAsUser } = useAuth();
  const { toast } = useToast();
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  
  // Optimized loading state management to prevent flicker
  useEffect(() => {
    if (isInitialized) {
      // Use requestAnimationFrame to batch state updates and prevent flicker
      requestAnimationFrame(() => {
        setInitialLoading(false);
      });
    }
  }, [isInitialized]);
  
  const handleLogout = useCallback(async (e: React.MouseEvent) => {
    e.preventDefault(); // Prevent any default navigation
    
    if (isSigningOut) return; // Prevent multiple clicks
    
    setIsSigningOut(true);
    try {
      console.log("Logout button clicked - starting sign out process");
      await signOut();
      navigate('/');
    } catch (error) {
      console.error("Error signing out:", error);
      toast({
        title: "Sign out failed",
        description: "An error occurred while signing out. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSigningOut(false);
    }
  }, [isSigningOut, signOut, navigate, toast]);
  
  // Stable computation of showProtectedLinks
  const showProtectedLinks = useMemo(() => 
    isAuthenticated && !isLoading && !isSigningOut, 
    [isAuthenticated, isLoading, isSigningOut]
  );
  
  const handleNavigate = useCallback((path: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    navigate(path);
  }, [navigate]);
  
  // Check if a path is active (for highlighting current section)
  const isActive = useCallback((path: string) => {
    // For paths like /library/* we want to match the base path
    if (path === '/library') {
      return location.pathname.startsWith(path);
    }
    return location.pathname === path;
  }, [location.pathname]);

  // Simplified loading condition to prevent flicker
  if (!isInitialized || initialLoading) {
    return <LoadingNavbar handleNavigate={handleNavigate} />;
  }
  
  return (
    <>
      <header className="bg-gradient-to-r from-yarn-quartz to-yarn-ivory border-b border-yarn-orchid/10 shadow-sm backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 flex justify-between items-center h-16 lg:grid lg:grid-cols-3">
          <div className="lg:justify-self-start">
            <Button variant="ghost" className="p-0 text-primary hover:text-primary/80 hover:bg-white/30" onClick={handleNavigate("/")}>
              <div className="flex items-center">
                <Logo variant="light" showTagline={true} size="sm" />
              </div>
            </Button>
          </div>
          
          <div className="justify-self-center hidden lg:block">
            <DesktopNavMenu 
              isActive={isActive} 
              handleNavigate={handleNavigate} 
              showProtectedLinks={showProtectedLinks} 
            />
          </div>
          
          <div className="lg:justify-self-end">
            <AuthButtons 
              isSigningOut={isSigningOut} 
              showProtectedLinks={showProtectedLinks} 
              handleLogout={handleLogout} 
              handleNavigate={handleNavigate}
            />
          </div>
        </div>
      </header>
      
      {isAdmin && viewAsUser && (
        <div
          className="sticky top-16 z-40 flex items-center justify-center gap-3 px-4 py-2 text-sm font-medium"
          style={{ background: '#FEF3C7', color: '#92400E', borderBottom: '1px solid #F59E0B' }}
        >
          <span>Admin view: you are browsing as a regular user</span>
          <button
            onClick={toggleViewAsUser}
            className="underline underline-offset-2 hover:opacity-70 transition-opacity"
          >
            Return to Admin View
          </button>
        </div>
      )}

      <MobileNavbar
        isActive={isActive}
        handleNavigate={handleNavigate}
        showProtectedLinks={showProtectedLinks}
      />
      
      {/* Spacer for mobile navbar - includes safe area for notch devices */}
      <div className="lg:hidden h-16" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}></div>
    </>
  );
};

NavbarComponent.displayName = 'Navbar';

export const Navbar = memo(NavbarComponent);
